import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { CommentsDataService, CommentsColumnsService } from '../../../services';
import { Comment, CommentsResult } from '../../../interfaces';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-comment-form',
  templateUrl: './comment-form.component.html',
  styleUrls: ['./comment-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class CommentFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public comment: Comment = {};
  public commentForm: FormGroup = new FormGroup({});
  public comments: Comment[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;

  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private commentsDataService: CommentsDataService,
    public commentsColumnsService: CommentsColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'CommentFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Comment - Edit');
        this.getComment(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.comment !== 'undefined') {
      this.comment = this.dynamicDialogConfig.data.comment;
    }
    if (typeof this.comment.id === 'undefined' || this.comment.id === null) {
      this.comment.id = null;
    }
    this.getComments();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'CommentFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'CommentFormComponent');
  }

  getComment(id: number): void {
    this.isLoading = true;
    this.commentsDataService.getComment(id).subscribe((response: Comment|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.comment = response;
        this.prepareFrom();
      }
    });
  }

  getComments(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.comments !== 'undefined' && this.dynamicDialogConfig.data.comments.length > 0) {
      this.comments = this.dynamicDialogConfig.data.comments;
      return;
    }
    this.commentsDataService.getComments(this.parameters).subscribe((response: CommentsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.comments = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.commentForm = new FormGroup({
      id: new FormControl(this.comment.id, []),
      entity: new FormControl(this.comment.entity, [Validators.required, ]),
      foreignKey: new FormControl(this.comment.foreignKey, [Validators.required, ]),
      pseudo: new FormControl(this.comment.pseudo, [Validators.required, ]),
      age: new FormControl(this.comment.age, [Validators.required, ]),
      city: new FormControl((typeof this.comment.city === 'number') ? this.comment.city : this.comment.city?.id, []),
      icon: new FormControl(this.comment.icon, [Validators.required, ]),
      subject: new FormControl(this.comment.subject, [Validators.required, ]),
      body: new FormControl(this.comment.body, [Validators.required, ]),
      status: new FormControl(this.comment.status, [Validators.required, ]),
    });
  }

  list() {
    this.router.navigate(['/comments']);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.commentForm.controls).forEach((field: any) => {
        const control = this.commentForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.commentForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.comment = this.commentForm.value;
    this.disableSubmit = true;
    console.log('comment', this.comment);
    if (typeof this.comment.id !== 'undefined' && this.comment.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.commentsDataService.updateComment(this.comment).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('COMMENTUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMMENTNOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.commentsDataService.addComment(this.comment).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('COMMENTCREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COMMENTNOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.commentForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'comments',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.commentForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }

}

@Component({
  selector: 'app-comment-form',
  templateUrl: './comment-form.component.html',
  styleUrls: ['./comment-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogCommentFormComponent extends CommentFormComponent {
}

